/**
 * BroadcastChannel Service - Cross-Tab Communication
 *
 * Synchronizes Redux state across multiple tabs from the same user.
 * Uses the BroadcastChannel API (supported in all modern browsers).
 *
 * Usage:
 *   import { broadcastChannel } from './services/broadcastChannelService';
 *   broadcastChannel.on('REDUX_ACTION', (action) => dispatch(action));
 *   broadcastChannel.broadcastAction(updateBatch({ id: 45, ... }));
 */

class BroadcastChannelService {
  constructor(channelName = 'wine2trace-sync') {
    this.channelName = channelName;
    this.channel = null;
    this.listeners = new Map();
    this.isSupported = typeof BroadcastChannel !== 'undefined';

    if (this.isSupported) {
      this.openChannel();
      console.log('✅ BroadcastChannel initialized:', channelName);
    } else {
      console.warn('⚠️ BroadcastChannel not supported, multi-tab sync disabled');
    }
  }

  /**
   * (Re)open the underlying native channel. Idempotent: a no-op if already open.
   * Lazily called by on()/postMessage() so an accidental close() is never fatal.
   */
  openChannel() {
    if (!this.isSupported || this.channel) return;
    this.channel = new BroadcastChannel(this.channelName);
    this.channel.onmessage = this.handleMessage.bind(this);
  }

  /**
   * Handle incoming messages from other tabs
   */
  handleMessage(event) {
    const { type, payload } = event.data;

    // Call registered listener for this type
    const listener = this.listeners.get(type);
    if (listener) {
      try {
        listener(payload);
      } catch (error) {
        console.error(`Error handling broadcast message type '${type}':`, error);
      }
    }
  }

  /**
   * Register a listener for specific event type
   * @param {string} type - Event type (e.g., 'REDUX_ACTION', 'CACHE_INVALIDATE')
   * @param {Function} callback - Callback function
   */
  on(type, callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }
    // Re-open lazily in case the channel was closed (e.g. a stale cleanup).
    this.openChannel();
    this.listeners.set(type, callback);
  }

  /**
   * Unregister a listener
   * @param {string} type - Event type
   */
  off(type) {
    this.listeners.delete(type);
  }

  /**
   * Broadcast message to other tabs
   * @param {string} type - Message type
   * @param {Object} payload - Message payload
   */
  postMessage(type, payload) {
    if (!this.isSupported) return;
    // Re-open lazily in case the channel was closed (e.g. a stale cleanup).
    this.openChannel();
    if (!this.channel) return;

    try {
      this.channel.postMessage({ type, payload });
    } catch (error) {
      console.error('Failed to broadcast message:', error);
    }
  }

  /**
   * Broadcast Redux action to other tabs
   * @param {Object} action - Redux action object
   */
  broadcastAction(action) {
    this.postMessage('REDUX_ACTION', action);
  }

  /**
   * Broadcast cache invalidation to other tabs
   * @param {Object} affectedEntities - Affected entities from backend
   */
  broadcastCacheInvalidation(affectedEntities) {
    this.postMessage('CACHE_INVALIDATE', affectedEntities);
  }

  /**
   * Close the channel (cleanup)
   */
  close() {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.listeners.clear();
  }

  /**
   * Check if BroadcastChannel is supported
   */
  get supported() {
    return this.isSupported;
  }
}

// Export singleton instance
export const broadcastChannel = new BroadcastChannelService();
export default broadcastChannel;
